import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useMgmtOptions } from "../hooks/useMgmtOptions";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { formatCompactINR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Target, Save, Trash2 } from "lucide-react";

function nowMonth() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

const NUMS = [
  ["ad_spend_budget", "Ad Spend Budget ₹"], ["lead_goal", "Lead Goal"], ["qualified_lead_goal", "Qualified Lead Goal"],
  ["consultation_goal", "Consultation Goal"], ["sale_goal", "Sale/Treatment Goal"], ["revenue_goal", "Revenue Goal ₹"],
  ["cpl_target", "CPL Target ₹"], ["qualified_cpl_target", "Qualified CPL Target ₹"], ["roas_target", "ROAS Target"],
];

export default function Goals() {
  const opts = useMgmtOptions();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState({ period_type: "month", period_key: nowMonth(), branch_id: "", vertical: "", service: "", employee_id: "" });

  const load = useCallback(async () => { const { data } = await api.get("/mgmt/goals"); setGoals(data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form };
      NUMS.forEach(([k]) => { payload[k] = Number(form[k] || 0); });
      await api.post("/mgmt/goals", payload);
      toast.success("Goal saved"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/mgmt/goals/${id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error("Failed"); } };

  const inp = "bg-[#0F172A] border-white/10 text-white h-10 focus-visible:ring-[#38BDF8]";
  const branchName = (id) => opts.branches.find((b) => b.id === id)?.name || (id ? id : "All");

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Goal Management" subtitle="Set targets by month, branch, vertical, service or employee — the dashboard compares actuals automatically" />

      {isAdmin && (
        <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6 space-y-4">
          <div className="flex items-center gap-2"><Target className="w-5 h-5 text-[#38BDF8]" /><h2 className="font-display text-lg font-semibold">Set / Update Goal</h2></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Period Type</Label>
              <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200"><SelectItem value="month">Month</SelectItem><SelectItem value="week">Week</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Period Key</Label>
              <Input data-testid="goal-period-key" value={form.period_key} onChange={(e) => setForm({ ...form, period_key: e.target.value })} placeholder="2026-08" className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Branch (opt)</Label>
              <Select value={form.branch_id || "all"} onValueChange={(v) => setForm({ ...form, branch_id: v === "all" ? "" : v })}>
                <SelectTrigger className={inp}><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-60"><SelectItem value="all">All Branches</SelectItem>{opts.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Vertical (opt)</Label>
              <Select value={form.vertical || "all"} onValueChange={(v) => setForm({ ...form, vertical: v === "all" ? "" : v })}>
                <SelectTrigger className={inp}><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200"><SelectItem value="all">All Verticals</SelectItem>{opts.verticals.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NUMS.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label>
                <Input data-testid={`goal-${k}`} type="number" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} /></div>
            ))}
          </div>
          <Button data-testid="save-goal-btn" onClick={save} className="h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full px-8"><Save className="w-4 h-4 mr-2" />Save Goal</Button>
        </div>
      )}

      <div className="rounded-xl bg-[#1E293B] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
            <th className="text-left py-2.5 px-3">Period</th><th className="text-left py-2.5 px-3">Scope</th><th className="text-right py-2.5 px-3">Ad Budget</th>
            <th className="text-right py-2.5 px-3">Leads</th><th className="text-right py-2.5 px-3">Qual</th><th className="text-right py-2.5 px-3">Consult</th>
            <th className="text-right py-2.5 px-3">Sales</th><th className="text-right py-2.5 px-3">Revenue</th><th className="text-right py-2.5 px-3">CPL</th><th className="text-right py-2.5 px-3">ROAS</th><th className="py-2.5 px-3"></th></tr></thead>
          <tbody>
            {goals.map((g) => (
              <tr key={g.id} className="border-b border-white/5">
                <td className="py-2.5 px-3 text-slate-200">{g.period_key}</td>
                <td className="py-2.5 px-3 text-xs text-slate-400">{branchName(g.branch_id)}{g.vertical ? ` · ${g.vertical}` : ""}{g.service ? ` · ${g.service}` : ""}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatCompactINR(g.ad_spend_budget)}</td>
                <td className="py-2.5 px-3 text-right tabular">{g.lead_goal}</td>
                <td className="py-2.5 px-3 text-right tabular">{g.qualified_lead_goal}</td>
                <td className="py-2.5 px-3 text-right tabular">{g.consultation_goal}</td>
                <td className="py-2.5 px-3 text-right tabular">{g.sale_goal}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatCompactINR(g.revenue_goal)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatCompactINR(g.cpl_target)}</td>
                <td className="py-2.5 px-3 text-right tabular">{g.roas_target}x</td>
                <td className="py-2.5 px-3 text-right">{isAdmin && <button onClick={() => del(g.id)} className="text-[#EF4444] hover:opacity-70"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
            {goals.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-slate-500">No goals configured yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
